import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Modal, Button } from '../../../components/ui';
import { fetchAPI } from '../../../services/apiClient';
import { Wallet, Package, ArrowUpCircle, ArrowDownCircle, ChevronDown, ChevronRight } from 'lucide-react';

// ── Tipos ──────────────────────────────────────────────

interface SaldoBanquero {
  saldo_actual: number;
  total_vendido: number;
  total_comision: number;
  total_premios: number;
  abonos_banquero: number;
  pagos_banquero: number;
  limite_visual_movimientos: number;
}

interface MovimientoCuenta {
  id: string;
  fecha: string;
  tipo: string;       // ABONO_BANQUERO | PAGO_BANQUERO
  concepto: string;
  monto: number;      // signed: abono < 0, pago > 0
  saldo_resultante: number;
}

interface PaqueteResumen {
  paquete_id: string;
  paquete_nombre: string;
  local_ids: string[];
}

interface VentasPaquete {
  paquete_reglas_id: string | null;
  paquete_nombre: string;
  locales_count: number;
  total_vendido: string;
  total_premios: string;
  total_comision: string;
  saldo_paquete: string;
}

interface SorteoDetalle {
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  total_vendido: string;
  total_comision: string;
  total_premios: string;
}

interface DetallePaquete {
  paquete_id: string;
  paquete_nombre: string;
  total_vendido: string;
  total_comision: string;
  total_premios: string;
  sorteos: SorteoDetalle[];
}

const fmt = (n: number) => Math.abs(Math.trunc(n)).toLocaleString('es-CR');

// Etiquetas desde la perspectiva del BANQUERO
const TIPO_LABEL: Record<string, string> = {
  ABONO_BANQUERO: 'Yo pagué',
  PAGO_BANQUERO: 'Me pagaron',
};
const TIPO_BADGE: Record<string, string> = {
  ABONO_BANQUERO: 'text-green-700 bg-green-50 border-green-200',
  PAGO_BANQUERO: 'text-blue-700 bg-blue-50 border-blue-200',
};

export const BanqueroSaldosPage: React.FC = () => {
  const [saldo, setSaldo] = useState<SaldoBanquero | null>(null);
  const [movimientos, setMovimientos] = useState<MovimientoCuenta[]>([]);
  const [paquetes, setPaquetes] = useState<PaqueteResumen[]>([]);
  const [ventasPaquete, setVentasPaquete] = useState<VentasPaquete[]>([]);
  const [detalle, setDetalle] = useState<DetallePaquete[]>([]);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal "Yo pago" (abono entregado a la banca)
  const [modalOpen, setModalOpen] = useState(false);
  const [pagoMonto, setPagoMonto] = useState('');
  const [pagoRef, setPagoRef] = useState('');
  const [pagoEnviando, setPagoEnviando] = useState(false);
  const [pagoMsg, setPagoMsg] = useState('');

  // NOTA: Los pagos recibidos de la banca solo los registra la BANCA
  // (POST /api/saldos/banqueros/{id}/pago). El banquero solo los visualiza.

  const cargarSaldoYMovs = useCallback(async () => {
    const [s, movs] = await Promise.all([
      fetchAPI<SaldoBanquero>('/banquero/cuenta/saldo'),
      fetchAPI<MovimientoCuenta[]>('/banquero/cuenta/movimientos'),
    ]);
    setSaldo(s);
    setMovimientos(movs);
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [paqs, ventas, det] = await Promise.all([
        fetchAPI<PaqueteResumen[]>('/paquetes-reglas/resumen-banquero'),
        fetchAPI<VentasPaquete[]>('/usuarios/banquero/resumen'),
        fetchAPI<DetallePaquete[]>('/banquero/cuenta/detalle-paquetes'),
        cargarSaldoYMovs(),
      ]);
      setPaquetes(paqs);
      setVentasPaquete(ventas);
      setDetalle(det);
    } catch (e: any) {
      setError(e.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [cargarSaldoYMovs]);

  useEffect(() => { cargar(); }, [cargar]);

  // Validación monto > 0
  const pagoMontoNum = parseFloat(pagoMonto);
  const pagoMontoValido = Number.isFinite(pagoMontoNum) && pagoMontoNum > 0;
  const pagoErrorInline = pagoMonto !== '' && !pagoMontoValido
    ? 'El monto debe ser mayor a cero'
    : '';

  const enviarPago = async () => {
    setPagoMsg('');
    if (!pagoMontoValido) {
      setPagoMsg('El monto debe ser mayor a cero');
      return;
    }
    setPagoEnviando(true);
    try {
      await fetchAPI('/banquero/cuenta/abono', {
        method: 'POST',
        body: JSON.stringify({ monto: pagoMontoNum, referencia: pagoRef }),
      });
      setPagoMonto('');
      setPagoRef('');
      setModalOpen(false);
      await cargarSaldoYMovs();
    } catch (e: any) {
      setPagoMsg(e.message || 'Error al registrar pago');
    } finally {
      setPagoEnviando(false);
    }
  };

  const abrirModalPago = () => {
    setPagoMonto('');
    setPagoRef('');
    setPagoMsg('');
    setModalOpen(true);
  };

  const pagosRecibidos = useMemo(
    () => movimientos.filter((m) => m.tipo === 'PAGO_BANQUERO'),
    [movimientos],
  );

  if (loading && !saldo) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Saldos</h1>
        <p className="text-gray-500">
          Tu saldo con la banca y resumen de paquetes asignados.
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Tarjetas resumen: Yo pagué · Me pagaron · Saldo neto */}
      {saldo && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <ArrowUpCircle size={22} className="text-green-700" />
              </div>
              <div>
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">Yo pagué</p>
                <p className="text-2xl font-bold text-green-700">
                  ₡{fmt(saldo.abonos_banquero)}
                </p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <ArrowDownCircle size={22} className="text-blue-700" />
              </div>
              <div>
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">Me pagaron</p>
                <p className="text-2xl font-bold text-blue-700">
                  ₡{fmt(saldo.pagos_banquero)}
                </p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                saldo.saldo_actual === 0
                  ? 'bg-gray-100'
                  : saldo.saldo_actual > 0
                  ? 'bg-red-100'
                  : 'bg-green-100'
              }`}>
                <Wallet size={22} className={
                  saldo.saldo_actual === 0
                    ? 'text-gray-500'
                    : saldo.saldo_actual > 0
                    ? 'text-red-700'
                    : 'text-green-700'
                } />
              </div>
              <div>
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">Saldo neto</p>
                <p className={`text-2xl font-bold ${
                  saldo.saldo_actual === 0
                    ? 'text-gray-700'
                    : saldo.saldo_actual > 0
                    ? 'text-red-700'
                    : 'text-green-700'
                }`}>
                  ₡{fmt(saldo.saldo_actual)}
                </p>
                <p className="text-[10px] text-gray-400">
                  {saldo.saldo_actual > 0
                    ? 'Le debes a la banca'
                    : saldo.saldo_actual < 0
                    ? 'La banca te debe'
                    : 'Sin saldo pendiente'}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Desglose técnico del saldo */}
      {saldo && (
        <Card>
          <h3 className="font-semibold text-gray-900 mb-3">Desglose del saldo</h3>
          <p className="text-xs text-gray-500 mb-3">
            Ventas y comisiones de sorteos cerrados. Premios: todos los pagados en tu banca.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-[11px] text-gray-500 uppercase tracking-wide">Ventas</p>
              <p className="text-lg font-bold text-blue-700">
                {saldo.total_vendido !== 0 ? '+' : ''}₡{fmt(saldo.total_vendido)}
              </p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-[11px] text-gray-500 uppercase tracking-wide">Premios</p>
              <p className="text-lg font-bold text-red-700">
                {saldo.total_premios !== 0 ? '−' : ''}₡{fmt(saldo.total_premios)}
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <p className="text-[11px] text-gray-500 uppercase tracking-wide">Comisión</p>
              <p className="text-lg font-bold text-yellow-700">
                {saldo.total_comision !== 0 ? '−' : ''}₡{fmt(saldo.total_comision)}
              </p>
            </div>
            <div className={`p-3 rounded-lg border-2 ${
              saldo.saldo_actual > 0
                ? 'bg-red-50 border-red-300'
                : saldo.saldo_actual < 0
                ? 'bg-green-50 border-green-300'
                : 'bg-gray-50 border-gray-300'
            }`}>
              <p className="text-[11px] text-gray-500 uppercase tracking-wide">Saldo neto</p>
              <p className={`text-lg font-bold ${
                saldo.saldo_actual > 0 ? 'text-red-700'
                : saldo.saldo_actual < 0 ? 'text-green-700'
                : 'text-gray-700'
              }`}>
                ₡{fmt(saldo.saldo_actual)}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Ventas por paquete con desglose por sorteo (colapsable) */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-1">Ventas por paquete</h3>
        <p className="text-xs text-gray-500 mb-3">
          Ventas y comisiones de sorteos cerrados. Premios: todos los pagados en tu banca.
          Click en un paquete para ver el desglose por sorteo.
        </p>
        {detalle.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            Sin ventas registradas aún
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-600 text-xs uppercase">
                  <th className="px-3 py-2 text-left">Paquete</th>
                  <th className="px-3 py-2 text-right">Vendido</th>
                  <th className="px-3 py-2 text-right">Premios</th>
                  <th className="px-3 py-2 text-right">Comisión</th>
                  <th className="px-3 py-2 text-right">Saldo</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {detalle.map((p) => {
                  const vendido = Number(p.total_vendido);
                  const premios = Number(p.total_premios);
                  const comision = Number(p.total_comision);
                  const saldoP = vendido - premios - comision;
                  const isOpen = expandidos.has(p.paquete_id);
                  const toggle = () => {
                    setExpandidos((prev) => {
                      const next = new Set(prev);
                      if (next.has(p.paquete_id)) next.delete(p.paquete_id);
                      else next.add(p.paquete_id);
                      return next;
                    });
                  };
                  return (
                    <React.Fragment key={p.paquete_id}>
                      <tr
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={toggle}
                      >
                        <td className="px-3 py-2 font-medium text-gray-900">
                          <span className="inline-flex items-center gap-1">
                            {isOpen ? (
                              <ChevronDown size={16} className="text-gray-500" />
                            ) : (
                              <ChevronRight size={16} className="text-gray-500" />
                            )}
                            {p.paquete_nombre}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-blue-600">₡{fmt(vendido)}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">₡{fmt(premios)}</td>
                        <td className="px-3 py-2 text-right font-mono text-yellow-700">₡{fmt(comision)}</td>
                        <td className={`px-3 py-2 text-right font-mono font-bold ${
                          saldoP > 0 ? 'text-red-600' : saldoP < 0 ? 'text-emerald-600' : 'text-gray-500'
                        }`}>₡{fmt(saldoP)}</td>
                      </tr>
                      {isOpen && (
                        p.sorteos.length === 0 ? (
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="px-3 py-2 pl-10 text-xs text-gray-500 italic">
                              Sin sorteos cerrados con ventas
                            </td>
                          </tr>
                        ) : (
                          p.sorteos.map((s) => {
                            const sv = Number(s.total_vendido);
                            const sp = Number(s.total_premios);
                            const sc = Number(s.total_comision);
                            const ss = sv - sp - sc;
                            return (
                              <tr key={s.sorteo_id} className="bg-gray-50">
                                <td className="px-3 py-1.5 pl-10 text-xs text-gray-700">
                                  {s.sorteo_nombre}
                                  <span className="ml-1 text-[10px] text-gray-400">({s.tipo_id})</span>
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-xs text-blue-600">₡{fmt(sv)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-xs text-red-600">₡{fmt(sp)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-xs text-yellow-700">₡{fmt(sc)}</td>
                                <td className={`px-3 py-1.5 text-right font-mono text-xs ${
                                  ss > 0 ? 'text-red-600' : ss < 0 ? 'text-emerald-600' : 'text-gray-500'
                                }`}>₡{fmt(ss)}</td>
                              </tr>
                            );
                          })
                        )
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Paquetes de reglas asignados */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Package size={18} className="text-gray-600" />
          <h3 className="font-semibold text-gray-900">
            Paquetes de reglas asignados
          </h3>
        </div>
        {paquetes.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            Sin paquetes asignados a tu banca.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-600 text-xs uppercase">
                  <th className="px-3 py-2 text-left">Paquete</th>
                  <th className="px-3 py-2 text-right">Locales asignados</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paquetes.map((p) => (
                  <tr key={p.paquete_id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {p.paquete_nombre}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-gray-700">
                      {p.local_ids.length}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ─── Sección Me pagan (solo lectura: la BANCA registra los pagos) ─── */}
      <Card>
        <div className="flex items-center gap-2 mb-1">
          <ArrowDownCircle size={20} className="text-blue-700" />
          <h3 className="font-semibold text-gray-900">Me pagan</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Pagos registrados por la banca a tu favor
        </p>
        {pagosRecibidos.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">
            Ningún pago registrado por la banca aún
          </p>
        ) : (
          <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b bg-gray-50 text-gray-600 text-xs uppercase">
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-right">Monto</th>
                  <th className="px-3 py-2 text-left">Referencia</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pagosRecibidos.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                      {m.fecha.slice(0, 16).replace('T', ' ')}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-medium text-blue-700">
                      ₡{fmt(m.monto)}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 max-w-[300px] truncate" title={m.concepto}>
                      {m.concepto}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Historial completo (ambos tipos) */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-2">
          Historial de movimientos
          {saldo && (
            <span className="ml-2 text-xs font-normal text-gray-500">
              (últimos {saldo.limite_visual_movimientos} — el saldo se calcula sobre el total)
            </span>
          )}
        </h3>
        {movimientos.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">
            Sin movimientos registrados
          </p>
        ) : (
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b bg-gray-50 text-gray-600 text-xs uppercase">
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-center">Tipo</th>
                  <th className="px-3 py-2 text-right">Monto</th>
                  <th className="px-3 py-2 text-left">Referencia</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {movimientos.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                      {m.fecha.slice(0, 16).replace('T', ' ')}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                        TIPO_BADGE[m.tipo] || 'text-gray-700 bg-gray-50 border-gray-200'
                      }`}>
                        {TIPO_LABEL[m.tipo] || m.tipo}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-medium text-gray-800">
                      ₡{fmt(m.monto)}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 max-w-[300px] truncate" title={m.concepto}>
                      {m.concepto}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal: Registrar abono a la banca */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Registrar abono a la banca"
        size="md"
      >
        <p className="text-sm text-gray-500 mb-4">
          Registro un pago que hago a la banca
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Monto a pagar
            </label>
            <input
              type="number"
              min="0.01"
              step="any"
              autoFocus
              value={pagoMonto}
              onChange={(e) => {
                setPagoMonto(e.target.value);
                setPagoMsg('');
              }}
              placeholder="0"
              className={`w-full px-3 py-2 border rounded-lg ${
                pagoErrorInline ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {pagoErrorInline && (
              <p className="text-[11px] text-red-600 mt-1">{pagoErrorInline}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Referencia (opcional)
            </label>
            <input
              type="text"
              value={pagoRef}
              onChange={(e) => setPagoRef(e.target.value)}
              placeholder="Nota o referencia"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          {pagoMsg && (
            <p className="text-sm text-red-600">{pagoMsg}</p>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={() => setModalOpen(false)}>
            Cancelar
          </Button>
          <button
            onClick={enviarPago}
            disabled={pagoEnviando || !pagoMontoValido}
            className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {pagoEnviando ? 'Enviando...' : 'Confirmar pago'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BanqueroSaldosPage;
